/**
 * Второй сорт газобетона: как его узнать и что сказать про него менеджеру и клиенту.
 *
 * 12.10.2026, Роман. На заводах появились блоки второго сорта — дешевле обычных,
 * но с условиями, которые нельзя забыть проговорить: плотности в партии вперемешку,
 * возможны сколы, и главное — блок невозвратный, после выезда с завода оплата 100%.
 *
 * Признак — слова «2 сорт» в названии позиции. Так названы все заведённые позиции
 * («Газобетон Istkult D500 200х250х625 мм 2 сорт»), и так же будут названы будущие:
 * заведут новую — предупреждение появится само, вручную ничего включать не нужно.
 * Отдельного поля в справочнике для этого нет, заводить его ради одного признака
 * дороже, чем смотреть на имя.
 *
 * Тексты согласованы с Романом 12.10.2026 до первой отправки клиенту.
 */

const VTOROY_SORT_MARKER = '2 сорт';
// Второй сорт Исткульта приезжает в двух видах упаковки — CubiBlock и заводской
// Исткульт, какая именно, зависит от размера блока (13.10.2026, Роман). Заранее это
// не известно, поэтому менеджеру сказано спросить логиста, а клиенту — что упаковка
// может быть любой из двух. Называть одну конкретную нельзя: 12.10 текст говорил
// «приходят в CubiBlock», и это обещание выполнялось не всегда. Остальные условия
// у Исткульта те же, что у Бонолита, поэтому это одна добавочная строка.
const ISTKULT_MARKER = 'istkult';

/** Позиция второго сорта. */
export const isVtoroySortName = (name) => (
  String(name || '').toLowerCase().includes(VTOROY_SORT_MARKER)
);

/** Позиция второго сорта Исткульта — у неё своя упаковка. */
export const isVtoroySortIstkult = (name) => {
  const text = String(name || '').toLowerCase();
  return text.includes(VTOROY_SORT_MARKER) && text.includes(ISTKULT_MARKER);
};

const imenemOf = (material) => (
  material?.name ?? material?.['Номенклатура'] ?? material?.nomenclature
);

/**
 * В заказе есть хотя бы одна позиция второго сорта.
 *
 * @param {Array} materials — позиции заказа; имя берётся из name или «Номенклатура»
 */
export const hasVtoroySort = (materials) => (
  (Array.isArray(materials) ? materials : []).some((material) => isVtoroySortName(imenemOf(material)))
);

/** В заказе есть второй сорт Исткульта — значит будет упаковка CubiBlock. */
export const hasVtoroySortIstkult = (materials) => (
  (Array.isArray(materials) ? materials : []).some((material) => isVtoroySortIstkult(imenemOf(material)))
);

/**
 * Памятка менеджеру — короткая, на экране расчёта. Это не текст для клиента:
 * менеджер должен проговорить условия голосом до того, как заказ уедет.
 */
// - Расхождение по размерам названо числом (13.10.2026, Роман: «обязательно нужно
//   добавить что возможны расхождения по размерам 1,5-2 см»). Без числа «возможны
//   расхождения» звучит как мелочь, а на кладке это заметная разница.
export const VTOROY_SORT_HINT_MANAGER = (
  '2 сорт. Плотности в партии вперемешку, возможны сколы и расхождения по размерам 1,5–2 см. '
  + 'Блок невозвратный: после выезда с завода оплата 100%, возврат и обмен невозможны. '
  + 'Водитель позвонит с погрузки, по требованию пришлёт фото.'
);

/** Та же памятка плюс упаковка, если в заказе второй сорт Исткульта. */
export const buildVtoroySortHintManager = (materials) => (
  hasVtoroySortIstkult(materials)
    ? `${VTOROY_SORT_HINT_MANAGER} Упаковка CubiBlock или Исткульт — зависит от размера, уточнить у логиста.`
    : VTOROY_SORT_HINT_MANAGER
);

/**
 * Абзац для коммерческого предложения — уходит заказчику как есть.
 *
 * @returns {string[]} строки абзаца в порядке показа
 */
export const buildVtoroySortClientLines = ({ istkult = false } = {}) => ([
  'Блоки второго сорта.',
  'В партии встречаются блоки разной плотности, возможны сколы.',
  'Возможны расхождения по размерам блоков — 1,5–2 см.',
  ...(istkult ? ['Упаковка может быть двух видов — CubiBlock или Исткульт.'] : []),
  'Товар невозвратный: после отгрузки с завода вернуть или обменять его нельзя, оплата 100%.',
  'Перед отправкой водитель свяжется с вами с погрузки и по запросу пришлёт фотографии блоков.',
]);

/**
 * Те же строки для сообщения клиенту: пусто, если второго сорта в заказе нет.
 * Вызывающий просто подмешивает результат в текст, ничего не проверяя.
 */
export const buildVtoroySortMessageLines = (materials) => (
  hasVtoroySort(materials)
    ? buildVtoroySortClientLines({ istkult: hasVtoroySortIstkult(materials) })
    : []
);

export default hasVtoroySort;
